import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 60_000;

/**
 * Reports when a newer build has been deployed than the one this tab is running.
 *
 * A single-page app fetches its JS bundle once and then never re-requests it, so
 * a long-open tab keeps running old code until something forces a reload. This
 * polls the deployed build stamp (`/version.json`, written at build time) and
 * compares it to the id baked into this bundle (`__APP_BUILD_ID__`). The caller
 * surfaces a banner so the user reloads when they're ready — no forced refresh
 * mid-task.
 *
 * It also treats a service-worker takeover (`controllerchange`) as an update,
 * which is what flushes clients still running the previous caching service
 * worker after the network-only SW ships. The first-ever install (no prior
 * controller) is ignored so brand-new visitors don't see a spurious banner.
 */
export function useVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const currentBuildId = __APP_BUILD_ID__;

    const check = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data: { buildId?: string } = await res.json();
        if (!cancelled && data.buildId && data.buildId !== currentBuildId) {
          setUpdateAvailable(true);
        }
      } catch {
        // Offline or a transient network blip — ignore and retry next tick.
      }
    };

    // Check once on mount (catches a deploy that landed while this tab was
    // loading), then poll.
    void check();
    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    window.addEventListener('focus', check);
    window.addEventListener('online', check);
    // Returning to a backgrounded tab (Ctrl+Tab, app switch) fires
    // visibilitychange but not always focus — re-check then too. `check` no-ops
    // while still hidden, so this only does work once the tab is visible.
    document.addEventListener('visibilitychange', check);

    const sw = navigator.serviceWorker;
    const hadController = Boolean(sw?.controller);
    const onControllerChange = () => {
      if (hadController) setUpdateAvailable(true);
    };
    sw?.addEventListener('controllerchange', onControllerChange);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', check);
      window.removeEventListener('online', check);
      document.removeEventListener('visibilitychange', check);
      sw?.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  return updateAvailable;
}
